'use client'

import { useState, useEffect, useMemo, useCallback } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useTranslations } from '@/lib/i18n'
import {
  Search,
  Filter,
  Plus,
  Grid3X3,
  List,
  Columns,
  Download,
  SortAsc,
  SortDesc,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Minus,
  ChevronDown,
  X,
  Calendar,
  User,
  Tag,
  Clock,
  FileText,
  Bug,
  Lightbulb,
  MoreHorizontal,
  Edit,
  Copy,
  Trash2,
  Check,
  Eye,
  Play,
  FolderOpen,
  Loader2
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { OrbitLoader } from '@/components/ui/orbit-loader'
import { getAuthHeaders } from '@/lib/api-utils'
import { IssueTimeTracker } from '@/components/issues/issue-time-tracker'
import dynamic from 'next/dynamic'
import { IssueCard } from '@/components/issues/issue-card'
import { IssueCardGrid } from '@/components/issues/issue-card-grid'
import { IssueFilterSidebar } from '@/components/issues/issue-filter-sidebar'
import { VirtualizedIssuesTable } from '@/components/issues/virtualized-issues-table'
import { AssigneeCell, PriorityTag, StatusTag, shortId, relativeTime } from '@/components/issues/issue-table-parts'
import { cn } from '@/lib/utils'
import { deleteIssue } from '@/lib/issue-service'
import { useToast } from '@/hooks/use-toast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { AlertTriangle } from 'lucide-react'

// Lazy load heavy modals to reduce initial bundle size

const IssueActionsModal = dynamic(
  () => import('@/components/issues/issue-actions-modal').then(mod => ({ default: mod.IssueActionsModal })),
  { ssr: false, loading: () => null }
)
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCurrentUser,
  useProjects,
  useTrackers,
  useIssueStatuses,
  usePriorities,
  useProjectVersions,
} from '@/hooks/use-common-api'

interface Issue {
  id: number
  number?: number
  subject: string
  description: string
  tracker: {
    id: number
    name: string
  }
  status: {
    id: number
    name: string
    is_closed: boolean
  }
  priority: {
    id: number
    name: string
  }
  author: {
    id: number
    name: string
  }
  assigned_to?: {
    id: number
    name: string
  }
  category?: {
    id: number
    name: string
  }
  fixed_version?: {
    id: number
    name: string
  }
  parent?: {
    id: number
    subject: string
  }
  children?: Issue[]
  project: {
    id: number
    name: string
  }
  start_date?: string
  due_date?: string
  done_ratio: number
  estimated_hours?: number
  spent_hours?: number
  created_on: string
  updated_on: string
  custom_fields?: Array<{
    id: number
    name: string
    value: string
  }>
}

interface Project {
  id: number
  name: string
  identifier: string
  description?: string
  status: number
}

interface FilterState {
  status: string[]
  tracker: string[]
  priority: string[]
  assignee: string[]
  version: string[]
  author: string[]
  category: string[]
  parent: string[]
}

interface SortConfig {
  field: keyof Issue | 'tracker.name' | 'status.name' | 'priority.name' | 'assigned_to.name'
  direction: 'asc' | 'desc'
}

interface IssuesListViewProps {
  projectId?: string  // Optional - if not provided, shows all issues
  showProjectSelector?: boolean  // Show project dropdown for filtering
  onRefreshTrigger?: number  // External trigger to force refresh
}

export function IssuesListView({ projectId, showProjectSelector = false, onRefreshTrigger }: IssuesListViewProps) {
  const t = useTranslations('issuesPage')
  const [issues, setIssues] = useState<Issue[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Use React Query hooks for cached API data - eliminates duplicate calls!
  const { data: currentUserData, isLoading: loadingCurrentUser } = useCurrentUser()
  const { data: projectsData, isLoading: loadingProjects } = useProjects(1000)
  const { data: trackersData, isLoading: loadingTrackers } = useTrackers()
  const { data: statusesData, isLoading: loadingStatuses } = useIssueStatuses()
  const { data: prioritiesData, isLoading: loadingPriorities } = usePriorities()
  
  // Project selector state - Persistent selected project
  const [selectedProjectId, setSelectedProjectId] = useState<string>(() => {
    // If projectId prop is provided, use it (for project-specific pages)
    if (projectId) return projectId
    // Otherwise load from localStorage
    if (typeof window !== 'undefined' && showProjectSelector) {
      return localStorage.getItem('issues-selected-project') || 'all'
    }
    return 'all'
  })
  
  // Load versions for selected project (conditional query)
  const effectiveProjectId = showProjectSelector ? selectedProjectId : projectId
  const { data: versionsData } = useProjectVersions(
    effectiveProjectId && effectiveProjectId !== 'all' ? effectiveProjectId : null
  )
  
  // Extract data from React Query hooks with defaults
  const currentUser = useMemo(() => {
    if (!currentUserData) return null
    return {
      id: currentUserData.id,
      name: currentUserData.name
    }
  }, [currentUserData])
  
  const projects = useMemo(() => projectsData?.projects || [], [projectsData])
  const trackers = useMemo(() => trackersData || [], [trackersData])
  const statuses = useMemo(() => statusesData || [], [statusesData])
  const priorities = useMemo(() => prioritiesData || [], [prioritiesData])
  const versions = useMemo(() => versionsData || [], [versionsData])
  
  // Persistent search term
  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('issues-search-term') || ''
    }
    return ''
  })
  
  // Debounced search term - only triggers fetch after user stops typing
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)
  
  // Debounce search term to prevent fetching on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 800) // 800ms delay - wait for user to stop typing
    
    return () => clearTimeout(timer)
  }, [searchTerm])
  
  // Persistent view mode
  const [viewMode, setViewMode] = useState<'list' | 'grid' | 'table'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('issues-view-mode')
      if (saved === 'list' || saved === 'grid' || saved === 'table') {
        return saved
      }
    }
    return 'table'
  })
  
  const [showFilters, setShowFilters] = useState(false)
  
  // Persistent sort config
  const [sortConfig, setSortConfig] = useState<SortConfig>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('issues-sort-config')
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch (e) {
          console.error('Failed to parse saved sort config:', e)
        }
      }
    }
    return { field: 'updated_on', direction: 'desc' }
  })

  // Client-side sort state for inline table (< 50 issues)
  type InlineSortColumn = 'id' | 'subject' | 'priority' | 'status' | 'assignee' | 'updated'
  const [inlineTableSort, setInlineTableSort] = useState<{
    column: InlineSortColumn | null
    direction: 'asc' | 'desc'
  }>({ column: null, direction: 'asc' })

  // Handle inline table sort
  const handleInlineTableSort = (column: InlineSortColumn) => {
    if (inlineTableSort.column === column) {
      setInlineTableSort({
        column,
        direction: inlineTableSort.direction === 'asc' ? 'desc' : 'asc'
      })
    } else {
      setInlineTableSort({ column, direction: 'asc' })
    }
  }

  // Sort indicator for a sortable table header
  const renderInlineSortIcon = (column: InlineSortColumn) => {
    if (inlineTableSort.column !== column) {
      return <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-0 group-hover:opacity-50 transition-opacity" />
    }
    return inlineTableSort.direction === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 inline" />
      : <ArrowDown className="h-3 w-3 ml-1 inline" />
  }

  // Sort issues for inline table
  const sortedInlineIssues = useMemo(() => {
    if (!inlineTableSort.column || issues.length >= 50) return issues

    return [...issues].sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (inlineTableSort.column) {
        case 'id':
          aValue = a.id
          bValue = b.id
          break
        case 'subject':
          aValue = a.subject.toLowerCase()
          bValue = b.subject.toLowerCase()
          break
        case 'priority':
          aValue = a.priority?.name?.toLowerCase() || ''
          bValue = b.priority?.name?.toLowerCase() || ''
          break
        case 'status':
          aValue = a.status?.name.toLowerCase() || ''
          bValue = b.status?.name.toLowerCase() || ''
          break
        case 'assignee':
          aValue = a.assigned_to?.name.toLowerCase() || 'zzz'
          bValue = b.assigned_to?.name.toLowerCase() || 'zzz'
          break
        case 'updated':
          aValue = new Date(a.updated_on).getTime() || 0
          bValue = new Date(b.updated_on).getTime() || 0
          break
        default:
          return 0
      }

      if (aValue < bValue) return inlineTableSort.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return inlineTableSort.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [issues, inlineTableSort])
  const [sidebarMenu, setSidebarMenu] = useState<{
    show: boolean
    issue: Issue | null
  }>({ show: false, issue: null })
  const [menuData, setMenuData] = useState<any>(null)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const router = useRouter()
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null)
  const { toast } = useToast()
  const [deleteTarget, setDeleteTarget] = useState<Issue | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Row-level delete: open confirmation, then call deleteIssue + refetch.
  const handleRowDelete = useCallback((issue: Issue) => {
    setDeleteTarget(issue)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      const result = await deleteIssue(deleteTarget.id)
      if (!result.success) {
        toast({
          variant: 'destructive',
          title: t('errorGeneric'),
          description: result.error || t('failedToDelete'),
        })
        setIsDeleting(false)
        return
      }
      toast({
        title: t('issueDeletedTitle'),
        description: t('issueDeletedDescription', { id: String(deleteTarget.number ?? deleteTarget.id) }),
      })
      // Drop the row optimistically so the table updates immediately.
      setIssues((prev) => prev.filter((row) => row.id !== deleteTarget.id))
      setDeleteTarget(null)
      // Then sync with server (covers cascade + totals).
      fetchIssues()
    } catch (err) {
      toast({
        variant: 'destructive',
        title: t('errorGeneric'),
        description: err instanceof Error ? err.message : t('failedToDelete'),
      })
    } finally {
      setIsDeleting(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteTarget, t, toast])

  const openCreateIssue = () => {
    const qs = projectId ? `?project=${projectId}` : ''
    router.push(`/issues/new${qs}`)
  }
  const openEditIssue = (id: string | number) => router.push(`/issues/${id}/edit`)

  // Project search state
  const [projectSearch, setProjectSearch] = useState('')
  
  // Users list (extracted from issues)
  const [users, setUsers] = useState<{id: number, name: string}[]>([])

  // Active filters - Load from localStorage on init
  const [filters, setFilters] = useState<FilterState>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('issues-filters')
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch (e) {
          console.error('Failed to parse saved filters:', e)
        }
      }
    }
    return {
      status: [],
      tracker: [],
      priority: [],
      assignee: [],
      version: [],
      author: [],
      category: [],
      parent: []
    }
  })

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('issues-filters', JSON.stringify(filters))
    }
  }, [filters])

  // Memoized callbacks to prevent unnecessary re-renders in child components
  const handleOpenMenu = useCallback((issue: any) => {
    setSidebarMenu({ show: true, issue })
    setMenuData({ statuses, priorities, trackers, members: users })
  }, [statuses, priorities, trackers, users])

  const handleCloseMenu = useCallback(() => {
    setSidebarMenu({ show: false, issue: null })
  }, [])

  const handleCloseFilters = useCallback(() => {
    setShowFilters(false)
  }, [])

  // Memoized search handler
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  // Memoized project search handler
  const handleProjectSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setProjectSearch(e.target.value)
  }, [])

  // Save search term to localStorage (debounced)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('issues-search-term', debouncedSearchTerm)
    }
  }, [debouncedSearchTerm])

  // Save view mode to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('issues-view-mode', viewMode)
    }
  }, [viewMode])

  // Save sort config to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('issues-sort-config', JSON.stringify(sortConfig))
    }
  }, [sortConfig])

  // Save selected project to localStorage (only if showProjectSelector is true)
  useEffect(() => {
    if (typeof window !== 'undefined' && showProjectSelector && !projectId) {
      localStorage.setItem('issues-selected-project', selectedProjectId)
    }
  }, [selectedProjectId, showProjectSelector, projectId])

  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const itemsPerPage = 25

  // Note: currentUser, projects, trackers, statuses, priorities, versions 
  // are now loaded automatically via React Query hooks!
  // No need for manual fetch functions or useEffect calls

  // Refetch issues when user returns to the page (e.g., after creating an issue)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, refetch issues
        fetchIssues()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [currentPage, sortConfig, filters, debouncedSearchTerm, selectedProjectId, projectId])

  const extractUsersFromIssues = (issues: any[]) => {
    const userMap = new Map<number, {id: number, name: string}>()

    issues.forEach(issue => {
      // Add assigned_to user
      if (issue.assigned_to) {
        if (!userMap.has(issue.assigned_to.id)) {
          userMap.set(issue.assigned_to.id, {
            id: issue.assigned_to.id,
            name: issue.assigned_to.name
          })
        }
      }

      // Add author if present
      if (issue.author) {
        if (!userMap.has(issue.author.id)) {
          userMap.set(issue.author.id, {
            id: issue.author.id,
            name: issue.author.name
          })
        }
      }
    })

    const extractedUsers = Array.from(userMap.values())
    if (extractedUsers.length > 0) {
      setUsers(extractedUsers)
    }
  }

  // Fetch the full organisation user list for the assignee/author filters.
  // Assignees are org-wide (not project memberships), so every user who joins
  // the organisation is selectable — matching the issue create/edit flows.
  const fetchOrgUsers = async () => {
    try {
      const authHeaders = getAuthHeaders()

      const res = await fetch('/api/users', { headers: authHeaders, cache: 'no-store' })
      if (!res.ok) return

      const data = await res.json()
      const orgUsers: { id: any; name: string; email: string }[] = (data.users || [])
        .filter((u: any) => u?.id)
        .map((u: any) => ({
          id: u.id,
          name: u.name || `${u.firstname || ''} ${u.lastname || ''}`.trim() || u.login || u.email || 'Unknown',
          email: u.email || '',
        }))

      if (orgUsers.length === 0) return

      // Append email in parentheses for users who share a display name so the
      // filter list stays unambiguous when duplicate names exist.
      const nameCount = new Map<string, number>()
      orgUsers.forEach(u => nameCount.set(u.name, (nameCount.get(u.name) || 0) + 1))
      const disambiguated = orgUsers.map(u => ({
        id: u.id,
        name: (nameCount.get(u.name) || 0) > 1 ? `${u.name} (${u.email})` : u.name,
      }))

      // Put the current user first so "assign to me" is one click away.
      const currentUserDisp = disambiguated.find(u => u.id === currentUser?.id)
      const sortedUsers = currentUser?.id && currentUserDisp
        ? [
            currentUserDisp,
            ...disambiguated.filter(u => u.id !== currentUser.id),
          ]
        : disambiguated

      setUsers(sortedUsers as { id: number; name: string }[])
    } catch (err) {
      // ignore failures silently - fallback extraction will apply
    }
  }

  const fetchIssues = async () => {
    // fetchIssues called
    const startTime = performance.now()
    
    // Don't clear issues immediately - this prevents the skeleton from showing
    // and losing focus on the search input
    setLoading(true)
    setError(null)

    try {
      const authHeaders = getAuthHeaders()
      if (!authHeaders || Object.keys(authHeaders).length === 0) {
        // No client-side authentication available. Set an error state and abort
        // the fetch instead of throwing - this avoids noisy stack traces in
        // production and lets the UI render a friendly access/unauthenticated state.
        setError('no_auth')
        return
      }

      // Build query parameters
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        offset: ((currentPage - 1) * itemsPerPage).toString(),
        sort: `${sortConfig.field}:${sortConfig.direction}`,
        _t: Date.now().toString() // Cache-busting timestamp
      })

      // Add project filter - use selectedProjectId if showProjectSelector is true, otherwise use projectId
      const effectiveProjectId = showProjectSelector ? selectedProjectId : projectId
      if (effectiveProjectId && effectiveProjectId !== 'all') {
        params.append('project_id', effectiveProjectId)
      }

      if (debouncedSearchTerm) {
        params.append('subject', `~${debouncedSearchTerm}`)
      }

      // Add filters
      if (filters.status.length > 0) {
        params.append('status_id', filters.status.join('|'))
      }
      if (filters.tracker.length > 0) {
        params.append('tracker_id', filters.tracker.join('|'))
      }
      if (filters.priority.length > 0) {
        params.append('priority_id', filters.priority.join('|'))
      }
      if (filters.assignee.length > 0) {
        params.append('assigned_to_id', filters.assignee.join('|'))
      }
      if (filters.author.length > 0) {
        params.append('author_id', filters.author.join('|'))
      }
      if (filters.version.length > 0) {
        params.append('fixed_version_id', filters.version.join('|'))
      }

      // Fetching issues with params
      const response = await fetch(`/api/issues?${params}`, {
        headers: authHeaders,
        cache: 'no-store'
      })

      if (!response.ok) {
        // Handle 403 Forbidden - Permission Denied
        if (response.status === 403) {
          setError('permission_denied')
          setIssues([])
          setTotalCount(0)
          return
        }
        throw new Error(`Failed to fetch issues: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
  // Received issues count
      
      // Ensure issues are ordered by most recently updated first as a client-side safety-net
      const fetched = data.issues || []
      const sorted = fetched.slice().sort((a: Issue, b: Issue) => {
        const ta = new Date(a.updated_on).getTime()
        const tb = new Date(b.updated_on).getTime()
        return tb - ta
      })
      setIssues(sorted)
      setTotalCount(data.total_count || 0)

      // Fall back to extracting users straight from the issues if the org
      // user list hasn't loaded yet (e.g. /api/users was slow or failed).
      if ((data.issues || []).length > 0 && users.length === 0) {
        Promise.resolve().then(() => extractUsersFromIssues(data.issues || []))
      }
      
  const endTime = performance.now()
  // fetchIssues completed in elapsed ms
    } catch (err) {
      console.error('[IssuesListView] fetchIssues error:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  // Note: Filter options (trackers, statuses, priorities, versions) are now loaded
  // automatically via React Query hooks - no need for separate fetch functions!

  // Fallback only: if the org user list never loaded, derive filter users
  // from the issues themselves. Never overrides a populated org user list.
  useEffect(() => {
    if (issues.length > 0 && users.length === 0) {
      extractUsersFromIssues(issues)
    }
  }, [issues, users.length])

  useEffect(() => {
    fetchIssues()
  }, [currentPage, sortConfig, filters, debouncedSearchTerm, selectedProjectId, projectId, onRefreshTrigger])

  // Load the org-wide user list once for the assignee / author filters.
  useEffect(() => {
    fetchOrgUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id])

  // Show project name under each issue only when there is no specific project selected.
  // If a project is selected via the dropdown (selectedProjectId !== 'all'), or
  // if this view is already scoped to a project (projectId provided), do not show the project name.
  const shouldShowProjectName = showProjectSelector ? (selectedProjectId === 'all') : (!projectId)

  const clearFilters = () => {
    setFilters({
      status: [],
      tracker: [],
      priority: [],
      assignee: [],
      version: [],
      author: [],
      category: [],
      parent: []
    })
  }

  const getActiveFilterCount = () => {
    return Object.values(filters).reduce((acc, curr) => acc + curr.length, 0)
  }

  const handleProjectSelect = useCallback((projectId: string) => {
    // Project selected
    
    // Immediately clear issues and set loading to show skeleton
    setIssues([])
    setLoading(true)
    
    setSelectedProjectId(projectId)
    setCurrentPage(1)
    setFilters({
      status: [],
      tracker: [],
      priority: [],
      assignee: [],
      version: [],
      author: [],
      category: [],
      parent: []
    })
  }, [])

  const filteredProjects = useMemo(() => {
    return projects.filter(project =>
      project.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
      project.identifier.toLowerCase().includes(projectSearch.toLowerCase())
    )
  }, [projects, projectSearch])

  if (loading && issues.length === 0) {
    return <OrbitLoader fullscreen label="Loading issues" />
  }

  if (error === 'permission_denied') {
    return (
      <div className="space-y-6">
        {/* Header with Project Selector - Always Visible */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 flex flex-wrap items-center gap-4">
            <Input
              placeholder={t('searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              className="max-w-sm"
              disabled
            />

            {/* Project Selector - Keep visible so user can switch projects */}
            {showProjectSelector && (
              <Select
                value={selectedProjectId}
                onValueChange={handleProjectSelect}
              >
                <SelectTrigger className="w-full sm:w-[280px]">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="truncate">
                      <SelectValue placeholder={t('allProjects')} />
                    </div>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground left-2.5" />
                      <Input
                        placeholder={t('searchProjectsPlaceholder')}
                        value={projectSearch}
                        onChange={handleProjectSearchChange}
                        className="pl-8 h-8"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t('allProjects')}</span>
                    </div>
                  </SelectItem>
                  {filteredProjects.map((project) => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium truncate">{project.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                  {filteredProjects.length === 0 && projectSearch && (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      {t('noProjectsFound')}
                    </div>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Access Denied Error Card */}
        <Card className="border-2 border-red-200 dark:border-red-800">
          <CardContent className="py-16 text-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-lg text-red-900 dark:text-red-100">
                  {t('accessDeniedTitle')}
                </h3>
                <p className="text-muted-foreground">
                  {t('accessDeniedMessage')}
                </p>
                {showProjectSelector && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('trySelectingDifferentProject') || 'Try selecting a different project from the dropdown above.'}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Empty state: No projects available
  if (showProjectSelector && projects.length === 0 && !loading) {
    return (
      <Card className="bg-card">
        <CardContent className="py-16 text-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
              <FolderOpen className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold text-lg">
                {t('noProjectAccessTitle')}
              </h3>
              <p className="text-muted-foreground max-w-md">
                {t('noProjectAccessMessage')}
              </p>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => window.location.href = '/projects'}>
                <FolderOpen className="h-4 w-4 mr-2" />
                {t('viewProjectsButton')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 flex flex-wrap items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <Input
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full"
              />
              {loading && issues.length > 0 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Project Selector */}
            {showProjectSelector && (
              <Select
                value={selectedProjectId}
                onValueChange={handleProjectSelect}
              >
                <SelectTrigger className="w-full sm:w-[280px]">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="truncate">
                      <SelectValue placeholder={t('allProjects')} />
                    </div>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground left-2.5" />
                      <Input
                        placeholder={t('searchProjectsPlaceholder')}
                        value={projectSearch}
                        onChange={handleProjectSearchChange}
                        className="pl-8 h-8"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t('allProjects')}</span>
                    </div>
                  </SelectItem>
                  {filteredProjects.map((project) => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium truncate">{project.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                  {filteredProjects.length === 0 && projectSearch && (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      {t('noProjectsFound')}
                    </div>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="relative"
            >
              <Filter className="h-4 w-4 mr-2" />
              {t('filtersTitle')}
              {getActiveFilterCount() > 0 && (
                <Badge variant="secondary" className="ml-2 px-1.5 py-0.5 text-xs">
                  {getActiveFilterCount()}
                </Badge>
              )}
            </Button>

            <div className="flex items-center space-x-1 border rounded-md overflow-hidden">
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className="px-3 rounded-none h-9"
              >
                <Columns className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="px-3 rounded-none h-9"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="px-3 rounded-none h-9"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
            </div>

            {!showProjectSelector && projectId && (
              <Button onClick={() => openCreateIssue()} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('newIssue')}
                </Button>
            )}
          </div>
        </div>

        {/* Issues List */}
        {!loading && issues.length > 0 && viewMode === 'list' && (
          <div className="space-y-3">
            {issues.map((issue) => (
              <IssueCard
                key={issue.id}
                issue={issue as any}
                onOpenMenu={handleOpenMenu}
                onDelete={handleRowDelete as any}
                showTimeTracker={true}
                showProjectName={shouldShowProjectName}
              />
            ))}
          </div>
        )}

        {/* Issues Table - compact: ID, Subject, Status, Assignee, Actions */}
        {/* Use virtualized table for better performance with large lists (50+ issues) */}
        {!loading && issues.length > 0 && viewMode === 'table' && (
          <>
            {issues.length >= 50 ? (
              <VirtualizedIssuesTable
                issues={issues}
                shouldShowProjectName={shouldShowProjectName}
                onOpenMenu={handleOpenMenu}
                onDelete={handleRowDelete as any}
              />
            ) : (
              <div className="overflow-x-auto rounded-lg border bg-card">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <th
                        className="w-20 cursor-pointer px-4 py-2.5 transition-colors hover:text-foreground group"
                        onClick={() => handleInlineTableSort('id')}
                      >
                        {t('tableHeaderId')}
                        {renderInlineSortIcon('id')}
                      </th>
                      <th
                        className="cursor-pointer px-4 py-2.5 transition-colors hover:text-foreground group"
                        onClick={() => handleInlineTableSort('subject')}
                      >
                        {t('tableHeaderSubject')}
                        {renderInlineSortIcon('subject')}
                      </th>
                      <th
                        className="hidden w-32 cursor-pointer px-4 py-2.5 transition-colors hover:text-foreground group lg:table-cell"
                        onClick={() => handleInlineTableSort('priority')}
                      >
                        {t('fields.priority')}
                        {renderInlineSortIcon('priority')}
                      </th>
                      <th
                        className="w-32 cursor-pointer px-4 py-2.5 transition-colors hover:text-foreground group"
                        onClick={() => handleInlineTableSort('status')}
                      >
                        {t('tableHeaderStatus')}
                        {renderInlineSortIcon('status')}
                      </th>
                      <th
                        className="hidden w-48 cursor-pointer px-4 py-2.5 transition-colors hover:text-foreground group md:table-cell"
                        onClick={() => handleInlineTableSort('assignee')}
                      >
                        {t('tableHeaderAssignee')}
                        {renderInlineSortIcon('assignee')}
                      </th>
                      <th
                        className="hidden w-28 cursor-pointer px-4 py-2.5 transition-colors hover:text-foreground group lg:table-cell"
                        onClick={() => handleInlineTableSort('updated')}
                      >
                        {t('tableHeaderUpdated')}
                        {renderInlineSortIcon('updated')}
                      </th>
                      <th className="sticky right-0 w-24 bg-muted/40 px-4 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {sortedInlineIssues.map((issue) => (
                      <tr key={issue.id} className="group transition-colors hover:bg-muted/40">
                        <td className="px-4 py-3 align-middle">
                          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                            #{issue.number ?? shortId(issue.id)}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <Link href={`/issues/${issue.id}`} className="block max-w-md">
                            <div className="flex items-center gap-1 min-w-0">
                              {issue.tracker?.name && (
                                <span className="shrink-0 text-xs text-muted-foreground">{issue.tracker.name} ·</span>
                              )}
                              <span className="truncate font-medium text-foreground transition-colors group-hover:text-primary">
                                {issue.subject}
                              </span>
                            </div>
                            {shouldShowProjectName && issue.project?.name && (
                              <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
                                {issue.project.name}
                              </div>
                            )}
                          </Link>
                        </td>
                        <td className="hidden px-4 py-3 align-middle lg:table-cell">
                          <PriorityTag name={issue.priority?.name} />
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <StatusTag name={issue.status?.name} isClosed={issue.status?.is_closed} />
                        </td>
                        <td className="hidden px-4 py-3 align-middle md:table-cell">
                          <AssigneeCell name={issue.assigned_to?.name} />
                        </td>
                        <td className="hidden whitespace-nowrap px-4 py-3 align-middle text-xs text-muted-foreground lg:table-cell">
                          {relativeTime(issue.updated_on) || '—'}
                        </td>
                        <td className="sticky right-0 bg-card px-4 py-3 align-middle group-hover:bg-muted/40">
                          <div className="flex items-center justify-end gap-0.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleRowDelete(issue)
                              }}
                              title={t('deleteIssue')}
                              aria-label={t('deleteIssue')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setSidebarMenu({ show: true, issue })
                                setMenuData({ statuses, priorities, trackers, members: users })
                              }}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Issues Grid */}
        {!loading && issues.length > 0 && viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {issues.map((issue) => (
              <IssueCardGrid
                key={issue.id}
                issue={issue as any}
                onOpenMenu={handleOpenMenu}
                onDelete={handleRowDelete as any}
                showProjectName={shouldShowProjectName}
              />
            ))}
          </div>
        )}

        {!loading && issues.length === 0 && (
          <Card className="bg-card">
            <CardContent className="py-16 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                  <FileText className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">
                    {searchTerm || getActiveFilterCount() > 0
                      ? t('noIssuesFound')
                      : t('noIssuesYet')
                    }
                  </h3>
                  <p className="text-muted-foreground">
                    {searchTerm || getActiveFilterCount() > 0
                      ? t('tryAdjustingFilters')
                      : showProjectSelector && selectedProjectId === 'all'
                      ? t('noIssuesAcrossAllProjects')
                      : t('createFirstIssue')
                    }
                  </p>
                </div>
                <div className="flex space-x-2">
                  {(searchTerm || getActiveFilterCount() > 0) && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchTerm('')
                        clearFilters()
                      }}
                    >
                      Clear Search & Filters
                    </Button>
                  )}
                  {!showProjectSelector && projectId && (
                    <Button onClick={() => openCreateIssue()}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Issue
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {!loading && totalCount > itemsPerPage && (
          <div className="flex items-center justify-between border-t border-border pt-4">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} issues
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, Math.ceil(totalCount / itemsPerPage)) }, (_, i) => {
                  const totalPages = Math.ceil(totalCount / itemsPerPage)
                  let pageNum: number

                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      className="w-10"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  )
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / itemsPerPage), prev + 1))}
                disabled={currentPage === Math.ceil(totalCount / itemsPerPage)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Filter Sidebar */}
      <IssueFilterSidebar
        isOpen={showFilters}
        onClose={handleCloseFilters}
        filters={filters}
        onFiltersChange={setFilters}
        trackers={trackers}
        statuses={statuses}
        priorities={priorities}
        users={users}
        versions={versions}
        currentUser={currentUser}
      />

      {/* Row-level Delete confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open && !isDeleting) setDeleteTarget(null) }}
      >
        <AlertDialogContent className="z-[10000]">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <AlertDialogTitle>{t('deleteIssueTitle')}</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              {deleteTarget && t('deleteIssueDescription', {
                id: String(deleteTarget.number ?? deleteTarget.id),
                subject: deleteTarget.subject,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteConfirm() }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('deleting') : t('deleteIssue')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Issue Actions Modal */}
      {sidebarMenu.show && sidebarMenu.issue && (
        <IssueActionsModal
            issue={sidebarMenu.issue as any}
            isOpen={sidebarMenu.show}
            onClose={() => setSidebarMenu({ show: false, issue: null })}
            projectId={sidebarMenu.issue.project?.id}
            onEditClick={() => {
              const id = sidebarMenu.issue?.id
              setSidebarMenu({ show: false, issue: null })
              if (id) openEditIssue(id)
            }}
            onActionComplete={async () => {
              setSidebarMenu({ show: false, issue: null })

              // Wait for Firebase to process the update (same delay as sprint view)
              await new Promise(resolve => setTimeout(resolve, 500))

              // Refetch to get updated data
              fetchIssues()
            }}
          />
      )}

    </>
  )
}
